import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { DEVICE_STATUS_HISTORY_QUERY_KEY } from '@/config';
import { DateRange } from '@/store';

export type DeviceStatusHistoryItem = {
    timestamp: string;
    batteryVoltage: number;
};

export type GetDeviceStatusHistoryResponse = {
    status: string;
    data: DeviceStatusHistoryItem[];
};

export type GetDeviceStatusHistoryParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
};

export const getDeviceStatusHistory = async (
    params: GetDeviceStatusHistoryParams,
    options?: RequestOptions
): Promise<DeviceStatusHistoryItem[]> => {
    const { data } = await axios.get<GetDeviceStatusHistoryResponse, GetDeviceStatusHistoryResponse>(
        '/fetch/device-status-history',
        {
            params,
            ...requestConfig(options),
        }
    );
    return data;
};

type UseDeviceStatusHistoryOptions = {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof getDeviceStatusHistory>, 'select'>;
};

export const useDeviceStatusHistory = ({
    spotterId,
    dateRange,
    config,
}: UseDeviceStatusHistoryOptions): CombinedQueryResult<DeviceStatusHistoryItem[]> =>
    useLiveSplitQuery({
        queryKeyPrefix: DEVICE_STATUS_HISTORY_QUERY_KEY,
        spotterId,
        dateRange,
        fetchRange: ({ viewToken, signal, ...params }) => getDeviceStatusHistory(params, { viewToken, signal }),
        // one of the newer device status entrypoints, so it excludes the end of the window it is given
        apiDateBounds: 'closedOpen',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet sorts this window oldest first across every device row it queried. Ordered here so the
         * trimming and clipping that reassemble the windows own the order they need.
         */
        responseSortOrder: 'unsorted',
        config,
    });
